import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from './supabaseClient';
import { useSession } from './SessionContext';
import {
  appendTrainingRecordEntry,
  fetchCompletedSoloFlightBookings,
  fetchTrainingRecordEntries,
} from './studentTrainingService';

const RECORD_TYPES = [
  ['flight', 'Flight record'],
  ['theory_exam', 'Theoretical knowledge exam'],
  ['skill_test', 'Skill test'],
  ['credit_review', 'School-reviewed prior credit'],
];

const OUTCOMES = [
  ['pass', 'Pass'],
  ['fail', 'Fail'],
  ['incomplete', 'Incomplete'],
];

export function formatLocalDateInputValue(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function dateLabel(value) {
  if (!value) return 'Date not recorded';
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function recordTypeLabel(type) {
  return RECORD_TYPES.find(([value]) => value === type)?.[1] || type;
}

function recordDetails(entry) {
  if (entry.record_type === 'flight') {
    const classification = entry.flight_classification || 'flight';
    const aircraft = entry.aircraft_registration
      ? ` · ${entry.aircraft_registration}`
      : '';
    const duration = entry.flight_duration_minutes
      ? ` · ${entry.flight_duration_minutes} min`
      : '';
    return `${classification}${aircraft}${duration}`;
  }
  if (entry.record_type === 'credit_review') {
    return `${entry.credit_minutes || 0} min reviewed · ${entry.credit_basis || 'basis not recorded'}`;
  }
  return [
    entry.subject,
    entry.outcome,
    entry.examiner_name ? `Examiner: ${entry.examiner_name}` : '',
  ].filter(Boolean).join(' · ');
}

export default function TrainingRecordEditor({
  booking,
  exercises = [],
  onSaved,
}) {
  const session = useSession();
  const organisationId = booking?.organisation_id || session?.profile?.organisation_id || '';
  const courseEnrolmentId = booking?.course_enrolment_id || '';
  const studentUserId = booking?.student_user_id || '';
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saveStatus, setSaveStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [recordType, setRecordType] = useState('flight');
  const [flightClassification, setFlightClassification] = useState(
    booking?.booking_type === 'training' ? 'dual' : 'solo'
  );
  const [recordedOn, setRecordedOn] = useState(
    booking?.date || formatLocalDateInputValue()
  );
  const [bookingId, setBookingId] = useState(
    booking?.booking_type === 'training' ? String(booking.id) : ''
  );
  const [exerciseId, setExerciseId] = useState('');
  const [flightDurationMinutes, setFlightDurationMinutes] = useState('');
  const [subject, setSubject] = useState('');
  const [outcome, setOutcome] = useState('pass');
  const [examinerName, setExaminerName] = useState('');
  const [creditMinutes, setCreditMinutes] = useState('');
  const [creditBasis, setCreditBasis] = useState('');
  const [comments, setComments] = useState('');
  const [correctionTargetId, setCorrectionTargetId] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');
  const [soloBookings, setSoloBookings] = useState([]);
  const [soloLoading, setSoloLoading] = useState(false);
  const [soloError, setSoloError] = useState('');
  const orderedEntries = useMemo(() => entries.slice().sort((left, right) => (
    String(left.recorded_on || '').localeCompare(String(right.recorded_on || '')) ||
    (Date.parse(left.assessed_at || left.created_at || '') || 0) -
    (Date.parse(right.assessed_at || right.created_at || '') || 0)
  )), [entries]);

  async function reloadEntries() {
    if (!organisationId || !courseEnrolmentId) {
      setLoadError('A linked course enrolment and organisation are required.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const result = await fetchTrainingRecordEntries(
        supabase,
        organisationId,
        courseEnrolmentId
      );
      if (result.error) throw result.error;
      setEntries(result.data || []);
    } catch (error) {
      setLoadError(`Could not load the training record. ${error?.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!organisationId || !courseEnrolmentId) {
        setLoadError('A linked course enrolment and organisation are required.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setLoadError('');
      try {
        const result = await fetchTrainingRecordEntries(
          supabase,
          organisationId,
          courseEnrolmentId
        );
        if (result.error) throw result.error;
        if (!cancelled) setEntries(result.data || []);
      } catch (error) {
        if (!cancelled) {
          setLoadError(`Could not load the training record. ${error?.message || 'Please try again.'}`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [organisationId, courseEnrolmentId]);

  useEffect(() => {
    let cancelled = false;
    async function loadSoloFlights() {
      if (recordType !== 'flight' || flightClassification !== 'solo') return;
      setSoloLoading(true);
      setSoloError('');
      try {
        const result = await fetchCompletedSoloFlightBookings(
          supabase,
          organisationId,
          studentUserId
        );
        if (result.error) throw result.error;
        if (!cancelled) {
          setSoloBookings(result.data || []);
          setSoloError('');
        }
      } catch (error) {
        if (!cancelled) {
          setSoloBookings([]);
          setSoloError(`Could not load completed student flights. ${error?.message || 'Please try again.'}`);
        }
      } finally {
        if (!cancelled) setSoloLoading(false);
      }
    }
    loadSoloFlights();
    return () => {
      cancelled = true;
    };
  }, [recordType, flightClassification, organisationId, studentUserId]);

  const correctionTargets = useMemo(() => {
    const correctedIds = new Set(
      orderedEntries.map((entry) => entry.supersedes_entry_id).filter(Boolean).map(String)
    );
    return orderedEntries.filter((entry) => (
      ['flight', 'theory_exam', 'skill_test', 'credit_review'].includes(entry.record_type)
      && !correctedIds.has(String(entry.id))
    ));
  }, [orderedEntries]);

  const activeBooking = useMemo(() => {
    if (flightClassification === 'dual') {
      return booking?.booking_type === 'training'
        ? {
          id: String(booking.id),
          date: booking.date,
          time: booking.time,
          aircraft_registration: booking.aircraft_registration || '',
        }
        : null;
    }
    return soloBookings.find((candidate) => String(candidate.id) === String(bookingId)) || null;
  }, [booking, bookingId, flightClassification, soloBookings]);

  async function handleCorrectionTargetChange(value) {
    setCorrectionTargetId(value);
    setCorrectionReason('');
    if (!value) {
      setRecordType('flight');
      setFlightClassification(booking?.booking_type === 'training' ? 'dual' : 'solo');
      setRecordedOn(booking?.date || formatLocalDateInputValue());
      setBookingId(booking?.booking_type === 'training' ? String(booking.id) : '');
      setExerciseId('');
      setFlightDurationMinutes('');
      setSubject('');
      setOutcome('pass');
      setExaminerName('');
      setCreditMinutes('');
      setCreditBasis('');
      setComments('');
      return;
    }
    const target = entries.find((entry) => String(entry.id) === String(value));
    if (!target) return;
    setRecordType(target.record_type);
    setFlightClassification(target.flight_classification || 'dual');
    setRecordedOn(target.recorded_on || formatLocalDateInputValue());
    setBookingId(target.booking_id ? String(target.booking_id) : '');
    setExerciseId(target.exercise_id || '');
    setFlightDurationMinutes(target.flight_duration_minutes || '');
    setSubject(target.subject || '');
    setOutcome(target.outcome || 'pass');
    setExaminerName(target.examiner_name || '');
    setCreditMinutes(target.credit_minutes || '');
    setCreditBasis(target.credit_basis || '');
    setComments(target.comments || '');
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (saving || loading || loadError || !courseEnrolmentId) return;
    if (recordType === 'flight' && flightClassification !== 'simulator' && !activeBooking) {
      setSaveError('Select an eligible completed flight booking.');
      return;
    }
    setSaving(true);
    setSaveError('');
    setSaveStatus('');
    try {
      const record = {
        courseEnrolmentId,
        recordType,
        recordedOn,
        comments: comments.trim(),
      };
      if (exerciseId) record.exerciseId = exerciseId;
      if (recordType === 'flight') {
        record.flightClassification = flightClassification;
        record.flightDurationMinutes = Number(flightDurationMinutes);
        if (flightClassification !== 'simulator') record.bookingId = Number(bookingId);
      } else if (recordType === 'theory_exam' || recordType === 'skill_test') {
        record.subject = subject.trim();
        record.outcome = outcome;
        if (examinerName.trim()) record.examinerName = examinerName.trim();
      } else if (recordType === 'credit_review') {
        record.creditMinutes = Number(creditMinutes);
        record.creditBasis = creditBasis.trim();
        if (subject.trim()) record.subject = subject.trim();
      }
      if (correctionTargetId) {
        record.supersedesEntryId = correctionTargetId;
        record.correctionReason = correctionReason.trim();
      }

      const result = await appendTrainingRecordEntry(supabase, record);
      if (result.error) throw result.error;
      setSaveStatus(correctionTargetId
        ? 'Correction appended. The earlier entry remains in the audit history.'
        : 'Training record entry saved.');
      await reloadEntries();
      onSaved?.(result.data);
    } catch (error) {
      setSaveError(`Could not save the training record. ${error?.message || 'Please try again.'}`);
    } finally {
      setSaving(false);
    }
  }

  const selectedCorrection = correctionTargets.find(
    (entry) => String(entry.id) === String(correctionTargetId)
  );
  const existingFlight = entries.some((entry) => (
    entry.record_type === 'flight' &&
    String(entry.booking_id || '') === String(activeBooking?.id || '') &&
    !entry.supersedes_entry_id
  ));

  return (
    <section className="training-record-editor" style={styles.panel} aria-labelledby="digital-training-record-heading">
      <h3 id="digital-training-record-heading" style={styles.heading}>
        Digital training record
      </h3>
      <p style={styles.help}>
        Instructor-signed entries are append-only. Corrections preserve the original.
        Progress, school review and formal licensing eligibility are separate.
      </p>

      {loading ? <div role="status" style={styles.message}>Loading the training record…</div> : null}
      {loadError ? <div role="alert" style={styles.error}>{loadError}</div> : null}
      {saveError ? <div role="alert" style={styles.error}>{saveError}</div> : null}
      {saveStatus ? <div role="status" style={styles.message}>{saveStatus}</div> : null}
      {soloError ? <div role="alert" style={styles.error}>{soloError}</div> : null}

      {!loading && !loadError ? (
        <form onSubmit={handleSubmit}>
          <label style={styles.field}>
            Correct an earlier entry (optional)
            <select
              aria-label="Correct an earlier training record"
              value={correctionTargetId}
              onChange={(event) => handleCorrectionTargetChange(event.target.value)}
              style={styles.select}
            >
              <option value="">Create a new record</option>
              {correctionTargets.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {dateLabel(entry.recorded_on)} · {recordTypeLabel(entry.record_type)}
                  {entry.subject ? ` · ${entry.subject}` : ''}
                  {entry.flight_classification ? ` · ${entry.flight_classification}` : ''}
                </option>
              ))}
            </select>
          </label>

          {correctionTargetId ? (
            <label style={styles.field}>
              Correction reason
              <textarea
                aria-label="Correction reason"
                value={correctionReason}
                onChange={(event) => setCorrectionReason(event.target.value)}
                required
                style={styles.textarea}
              />
            </label>
          ) : null}

          <div style={styles.grid}>
            <label style={styles.field}>
              Record type
              <select
                aria-label="Training record type"
                value={recordType}
                onChange={(event) => setRecordType(event.target.value)}
                disabled={Boolean(correctionTargetId)}
                style={styles.select}
              >
                {RECORD_TYPES.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
            <label style={styles.field}>
              Date
              <input
                aria-label="Training record date"
                type="date"
                value={recordedOn}
                onChange={(event) => setRecordedOn(event.target.value)}
                required
                style={styles.select}
              />
            </label>
          </div>

          {recordType === 'flight' ? (
            <>
              <div style={styles.grid}>
                <label style={styles.field}>
                  Flight classification
                  <select
                    aria-label="Flight classification"
                    value={flightClassification}
                    onChange={(event) => {
                      setFlightClassification(event.target.value);
                      setBookingId(event.target.value === 'dual' && booking?.booking_type === 'training'
                        ? String(booking.id)
                        : '');
                    }}
                    disabled={Boolean(correctionTargetId)}
                    style={styles.select}
                  >
                    <option value="dual">Dual</option>
                    <option value="solo">Solo</option>
                    <option value="simulator">Simulator</option>
                  </select>
                </label>
                <label style={styles.field}>
                  Actual flight duration (minutes)
                  <input
                    aria-label="Actual flight duration minutes"
                    type="number"
                    min="1"
                    max="1440"
                    step="1"
                    value={flightDurationMinutes}
                    onChange={(event) => setFlightDurationMinutes(event.target.value)}
                    required
                    style={styles.select}
                  />
                </label>
              </div>
              {flightClassification !== 'simulator' ? (
                <label style={styles.field}>
                  Completed flight booking
                  <select
                    aria-label="Completed flight booking"
                    value={bookingId}
                    onChange={(event) => setBookingId(event.target.value)}
                    disabled={Boolean(correctionTargetId) || (flightClassification === 'solo' && soloLoading)}
                    required
                    style={styles.select}
                  >
                    <option value="">
                      {flightClassification === 'solo'
                        ? (soloLoading ? 'Loading completed solo flights…' : 'Select a completed student flight')
                        : 'Select the training booking'}
                    </option>
                    {flightClassification === 'dual' && booking?.booking_type === 'training' ? (
                      <option value={String(booking.id)}>
                        {dateLabel(booking.date)} {booking.time || ''} · booking {booking.id}
                      </option>
                    ) : null}
                    {flightClassification === 'solo' && soloBookings.map((candidate) => (
                      <option key={candidate.id} value={String(candidate.id)}>
                        {dateLabel(candidate.date)} {candidate.time || ''} · {candidate.aircraft_registration || 'aircraft'}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              <label style={styles.field}>
                Syllabus exercise (optional)
                <select
                  aria-label="Training record exercise"
                  value={exerciseId}
                  onChange={(event) => setExerciseId(event.target.value)}
                  style={styles.select}
                >
                  <option value="">Not linked to one exercise</option>
                  {exercises.map((exercise) => (
                    <option key={exercise.id} value={exercise.id}>
                      {exercise.exercise_reference ? `${exercise.exercise_reference} · ` : ''}
                      {exercise.title}
                    </option>
                  ))}
                </select>
              </label>
              {existingFlight && !correctionTargetId ? (
                <p style={styles.message} role="status">
                  A flight record already exists for this booking. Select it above to append a correction.
                </p>
              ) : null}
            </>
          ) : null}

          {recordType === 'theory_exam' || recordType === 'skill_test' ? (
            <div style={styles.grid}>
              <label style={styles.field}>
                {recordType === 'theory_exam' ? 'Theory subject or paper' : 'Test name'}
                <input
                  aria-label={recordType === 'theory_exam' ? 'Theory subject' : 'Test name'}
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  required={recordType === 'theory_exam'}
                  style={styles.select}
                />
              </label>
              <label style={styles.field}>
                Result
                <select
                  aria-label="Training record result"
                  value={outcome}
                  onChange={(event) => setOutcome(event.target.value)}
                  required
                  style={styles.select}
                >
                  {OUTCOMES.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
              {recordType === 'skill_test' ? (
                <label style={styles.field}>
                  Examiner name (optional)
                  <input
                    aria-label="Examiner name"
                    value={examinerName}
                    onChange={(event) => setExaminerName(event.target.value)}
                    style={styles.select}
                  />
                </label>
              ) : null}
            </div>
          ) : null}

          {recordType === 'credit_review' ? (
            <div style={styles.grid}>
              <label style={styles.field}>
                Reviewed credit (minutes)
                <input
                  aria-label="Reviewed credit minutes"
                  type="number"
                  min="1"
                  max="60000"
                  step="1"
                  value={creditMinutes}
                  onChange={(event) => setCreditMinutes(event.target.value)}
                  required
                  style={styles.select}
                />
              </label>
              <label style={styles.field}>
                Credit basis
                <input
                  aria-label="Credit basis"
                  value={creditBasis}
                  onChange={(event) => setCreditBasis(event.target.value)}
                  required
                  style={styles.select}
                />
              </label>
              <label style={styles.field}>
                Related subject (optional)
                <input
                  aria-label="Credit subject"
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  style={styles.select}
                />
              </label>
            </div>
          ) : null}

          <label style={styles.field}>
            Instructor comments
            <textarea
              aria-label="Training record comments"
              value={comments}
              onChange={(event) => setComments(event.target.value)}
              maxLength={10000}
              style={styles.textarea}
            />
          </label>

          <p style={styles.disclaimer}>
            This record captures school training progress only. A test result or
            reviewed credit does not certify course completion or licensing eligibility.
          </p>
          <button
            type="submit"
            disabled={
              saving ||
              loading ||
              Boolean(loadError) ||
              (recordType === 'flight' &&
                ((existingFlight && !correctionTargetId) ||
                  (flightClassification !== 'simulator' && !bookingId) ||
                  !flightDurationMinutes)) ||
              (correctionTargetId && !correctionReason.trim()) ||
              (recordType === 'credit_review' && (!creditMinutes || !creditBasis.trim()))
            }
            style={styles.button}
          >
            {saving ? 'Saving record…' : correctionTargetId ? 'Append correction' : 'Save training record'}
          </button>
          {selectedCorrection ? (
            <p style={styles.help}>
              Preserving the earlier {recordTypeLabel(selectedCorrection.record_type).toLowerCase()}
              {' '}entry dated {dateLabel(selectedCorrection.recorded_on)}.
            </p>
          ) : null}
        </form>
      ) : null}

      <h4 style={styles.historyHeading}>Record history</h4>
      {entries.length === 0 && !loading && !loadError ? (
        <p style={styles.message}>No normalized training record entries are available yet.</p>
      ) : entries.length > 0 ? (
        <ol className="training-record-history" style={styles.historyList}>
          {orderedEntries.map((entry) => (
            <li key={entry.id} className="training-record-history-item" style={styles.historyItem}>
              <strong>{dateLabel(entry.recorded_on)} · {recordTypeLabel(entry.record_type)}</strong>
              {recordDetails(entry) ? <div>{recordDetails(entry)}</div> : null}
              {entry.assessor_name ? <div>Instructor sign-off: {entry.assessor_name}</div> : null}
              {entry.comments ? <div>{entry.comments}</div> : null}
              {entry.created_at || entry.assessed_at ? (
                <time dateTime={entry.assessed_at || entry.created_at}>
                  {new Date(entry.assessed_at || entry.created_at).toLocaleString()}
                </time>
              ) : null}
              {entry.supersedes_entry_id ? <div>Supersedes entry {entry.supersedes_entry_id}</div> : null}
              {entry.correction_reason ? <div>Correction: {entry.correction_reason}</div> : null}
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
}

const styles = {
  panel: {
    marginTop: 16,
    padding: 16,
    border: '1px solid rgba(120, 180, 255, 0.2)',
    borderRadius: 9,
    background: '#111c2e',
    color: '#edf4fc',
  },
  heading: { margin: '0 0 6px', color: '#eff6ff', fontSize: 16 },
  historyHeading: { margin: '18px 0 6px', color: '#dce9f7', fontSize: 14 },
  help: { color: '#aebfd2', fontSize: 12, lineHeight: 1.5 },
  message: { color: '#c8d7e8', fontSize: 12, lineHeight: 1.5 },
  error: { color: '#ffd9dc', fontSize: 12, lineHeight: 1.5 },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))',
    gap: 10,
    marginBottom: 10,
  },
  field: {
    display: 'grid',
    gap: 4,
    margin: '0 0 10px',
    color: '#c8d7e8',
    fontSize: 12,
  },
  select: {
    width: '100%',
    minHeight: 38,
    padding: '7px 9px',
    border: '1px solid #435a75',
    borderRadius: 6,
    background: '#101a2a',
    color: '#f2f7fc',
    font: 'inherit',
  },
  textarea: {
    minHeight: 64,
    padding: 8,
    border: '1px solid #435a75',
    borderRadius: 6,
    background: '#101a2a',
    color: '#f2f7fc',
    font: 'inherit',
    resize: 'vertical',
  },
  disclaimer: { color: '#f0dba1', fontSize: 11, lineHeight: 1.45 },
  button: {
    minHeight: 38,
    padding: '8px 14px',
    border: '1px solid #1581aa',
    borderRadius: 7,
    background: '#08799f',
    color: '#eaf6ff',
    font: 'inherit',
    fontWeight: 650,
    cursor: 'pointer',
  },
  historyList: { margin: 0, paddingLeft: 20 },
  historyItem: { margin: '8px 0', fontSize: 12, lineHeight: 1.5 },
};
